import { useTranslate } from '@im/react-localization';
import { FilterItem, FilterSetting } from '../advanceSearch/InterfaceTypes';
import { useFeatureFlagEnabled } from 'src/hooks/useFeatureFlagEnabled';
import { IM_PRICING_GDE_UI_HYBRID_ENABLED } from 'src/util/constants';

export function useFiltersItems(): FilterItem[] {
  const translate = useTranslate();
  return [
    {
      label: translate('VendorDeals.Vendor'),
      value: 'vendor',
    },
    {
      label: translate('VendorDeals.Reseller'),
      value: 'reseller',
    },
    {
      label: translate('VendorDeals.EndUser'),
      value: 'endUser',
    },
    {
      label: translate('VendorDeals.DealHeaderDetail'),
      value: 'dealHeaderDetail',
    },
    {
      label: translate('VendorDeals.DiscountLineDetail'),
      value: 'discountLineDetail',
    },
    {
      label: translate('Common.DateRange'),
      value: 'dateRange',
    },
  ];
}

export function useDefaultOperators(): FilterSetting['operators'] {
  const translate = useTranslate();
  return [
    { label: translate('Common.ConditionStartsWith'), value: 'Prefix' },
    { label: translate('Common.ConditionContains'), value: 'Contains' },
    { label: translate('Common.ConditionDoesNotContain'), value: 'Excludes' },
    { label: translate('Common.ConditionExact'), value: 'Exact' },
  ];
}

export function useFilterSettings(): FilterSetting[] {
  const translate = useTranslate();
  const defaultOperators = useDefaultOperators();
  const isHybridEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_HYBRID_ENABLED
  );
  return [
    {
      filterOptions: [
        {
          label: translate('VendorDeals.VendorName'),
          value: 'vendorName',
        },
        {
          label: translate('VendorDeals.VendorNumber'),
          value: 'vendorNumber',
        },
        {
          label: translate('VendorDeals.Program_Name'),
          value: 'programName',
        },
      ],
      operators: defaultOperators,
      value: 'vendor',
    },
    {
      filterOptions: [
        {
          label: translate('VendorDeals.VendorName'),
          value: 'vendorName',
        },
        {
          label: translate('VendorDeals.VendorNumber'),
          value: 'vendorNumber',
        },
        {
          label: translate('VendorDeals.Program_Name'),
          value: 'programName',
        },
      ],
      operators: defaultOperators,
      value: 'program',
    },
    {
      filterOptions: [
        { label: translate('VendorDeals.ResellerName'), value: 'resellerName' },
      ],
      operators: defaultOperators,
      value: 'reseller',
    },
    {
      filterOptions: [
        { label: translate('VendorDeals.EndUserName'), value: 'endUserName' },
      ],
      operators: defaultOperators,
      value: 'endUser',
    },
    {
      filterOptions: [
        {
          label: translate('VendorDeals.DealNo'),
          value: 'dealNumber',
        },
        {
          label: translate('VendorDeals.MasterContract'),
          value: 'masterContract',
        },
        ...(isHybridEnabled
          ? [
              {
                defaultOptionValue: 'HWSW',
                hideOperators: true,
                label: translate('Common.BusinessType'),
                value: 'businessType',
                valueOptions: [
                  { label: translate('AdvancedSearch.HWSW'), value: 'HWSW' },
                  {
                    label: translate('AdvancedSearch.Subscription'),
                    value: 'Subscription',
                  },
                  {
                    label: translate('AdvancedSearch.Hybrid'),
                    value: 'Hybrid',
                  },
                ],
              },
            ]
          : []),
        {
          defaultOptionValue: 'N',
          hideOperators: true,
          label: translate('VendorDeals.Orderable'),
          value: 'orderable',
          valueOptions: [
            { label: translate('Common.Yes'), value: 'Y' },
            { label: translate('Common.No'), value: 'N' },
          ],
        },
      ],
      operators: defaultOperators,
      value: 'dealHeaderDetail',
    },
    {
      filterOptions: [
        { label: 'VPN', value: 'discountLineDetailVPN' },
        {
          label: translate('VendorDeals.VendorNo'),
          value: 'discountLineDetailVendorNumber',
        },
        {
          label: translate('VendorDeals.ProductGroup'),
          value: 'discountLineDetailProductGroup',
        },
        ...[1, 2, 3, 4].map((number) => ({
          label: translate('VendorDeals.Text', { number }),
          value: `discountLineDetailText${number}`,
        })),
        ...(isHybridEnabled
          ? [
              {
                defaultOptionValue: 'subscription',
                hideOperators: true,
                label: translate('VendorProgram.ProductbusinessType'),
                value: 'ProductBusinessType',
                valueOptions: [
                  {
                    label: translate('AdvancedSearch.Subscription'),
                    value: 'subscription',
                  },
                  { label: translate('AdvancedSearch.HWSW'), value: 'HWSW' },
                ],
              },
            ]
          : []),
      ],
      operators: defaultOperators,
      value: 'discountLineDetail',
    },
    {
      filterOptions: [
        {
          label: translate('VendorProgram.ExpirationDate'),
          value: 'expirationDate',
        },
        {
          label: translate('VendorProgram.EffectiveDate'),
          value: 'effectiveDate',
        },
        {
          label: translate('VendorProgram.PriceProtectionDate'),
          value: 'priceProtectionDate',
        },
      ],
      operators: [
        { label: translate('Common.Range'), type: 'range', value: 'range' },
        { label: translate('Common.Is'), type: 'date', value: 'is' },
      ],
      value: 'dateRange',
    },
  ];
}

export function useSubscriptionFiltersItems(): FilterItem[] {
  const translate = useTranslate();
  return [
    {
      label: translate('VendorDeals.Vendor'),
      value: 'vendor',
    },
    {
      label: translate('VendorDeals.Program'),
      value: 'program',
    },
    {
      label: translate('VendorDeals.DealHeaderDetail'),
      value: 'dealHeaderDetail',
    },
  ];
}

export function useSubscriptionFilterSettings(): FilterSetting[] {
  const translate = useTranslate();
  return [
    {
      filterOptions: [
        {
          label: translate('VendorDeals.VendorName'),
          value: 'vendorName',
        },
      ],
      operators: [
        {
          label: translate('Common.ConditionExact'),
          type: 'autocomplete',
          value: 'Exact',
        },
      ],
      value: 'vendor',
    },
    {
      filterOptions: [
        {
          label: translate('VendorDeals.Program_Name'),
          value: 'programName',
        },
      ],

      operators: [
        {
          label: translate('Common.ConditionExact'),
          type: 'autocomplete',
          value: 'Exact',
        },
      ],
      value: 'program',
    },
    {
      filterOptions: [
        {
          label: translate('VendorDeals.DealNo'),
          value: 'dealNumber',
        },
      ],
      operators: [
        {
          label: translate('Common.ConditionExact'),
          type: 'autocomplete',
          value: 'Exact',
        },
      ],

      value: 'dealHeaderDetail',
    },
  ];
}
